import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { updateRequisitionStatusSuccess } from 'projects/requisition-management/src/app/store/requisitions/requisitions.actions';
import { combineLatest, concatMap, distinctUntilChanged, filter, map, mergeMap, of, take, withLatestFrom } from 'rxjs';

import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import { DataLayerObject, GTMEventItems } from 'ish-core/models/gtm/gtm.model';
import { LineItem } from 'ish-core/models/line-item/line-item.model';
import { Order } from 'ish-core/models/order/order.model';
import { GoogleTagManagerService } from 'ish-core/services/google-tag-manager/google-tag-manager.service';
import { selectCleanUrl, selectPreviousUrl } from 'ish-core/store/core/router/router.selectors';
import {
  addItemsToBasketSuccess,
  deleteBasketItem,
  updateBasketItemSuccess,
} from 'ish-core/store/customer/basket/basket.actions';
import { getCurrentBasket } from 'ish-core/store/customer/basket/basket.selectors';
import { createMultipleShippingOrderSuccess, createOrderSuccess } from 'ish-core/store/customer/orders/orders.actions';
import { createUserSuccess, loginUserSuccess } from 'ish-core/store/customer/user/user.actions';
import { mapToPayload, mapToPayloadProperty } from 'ish-core/utils/operators';

import {
  triggerBeginCheckoutGTMEvent,
  triggerGTMRemoveToCart,
  triggerPaymentMethodGTMEvent,
  triggerReviewCheckoutGTMEvent,
  triggerShippingMethodGTMEvent,
  triggeringSearchGTMEvent,
  triggeringSelectPromotionGTMEvent,
  triggeringViewItemGTMEvent,
  triggeringViewItemGTMEvents,
  triggeringViewPromotionGTMEvent,
} from './google-tag-manager.actions';

@Injectable()
export class GoogleTagManagerEffects {
  isTrackingAllowed: boolean;
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private googleTagManagerService: GoogleTagManagerService,
    private featureToggleService: FeatureToggleService
  ) {
    this.featureToggleService.enabled$('tracking').subscribe(enable => (this.isTrackingAllowed = enable));
  }
  /*Login User Event */
  loginUserGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(loginUserSuccess),
        map(() => {
          this.googleTagManagerService.triggeringEventOfUserIdEvent('login');
        })
      ),
    { dispatch: false }
  );
  /*Sign_up Event */
  signUpUserGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(createUserSuccess),
        map(() => {
          this.googleTagManagerService.triggeringEventOfSignUpEvent('sign_up');
        })
      ),
    { dispatch: false }
  );
  /*view promotion*/
  triggeringViewPromotionGTMEvent = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggeringViewPromotionGTMEvent),
        mapToPayload(),
        map(item => {
          const promotionContent: GTMEventItems = {
            creative_name: item.item[0].title.replace(/\.[^/.]+$/, '') || item.item[0].bannerTitle,
            creative_slot: item.item[0].contentType,
            promotion_name: item.item[0].name || item.item[0].caption,
            promotion_id: item.item[0].identifier,
          };
          this.googleTagManagerService.triggeringPromotionGTMEvent(item.event, [promotionContent]);
        })
      ),
    {
      dispatch: false,
    }
  );
  /*select promotion */
  triggeringSelectPromotionGTMEvent = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggeringSelectPromotionGTMEvent),
        mapToPayload(),
        map(item => {
          if (item?.item[0]?.productSKU) {
            this.googleTagManagerService.triggeringSelectPromotionGTMEvent(item.event, item.item);
          } else {
            const promotionContent: GTMEventItems = {
              link: item.item[0]?.link,
              creative_name: item?.item[0]?.title || item.item[0]?.bannerTitle,
              creative_slot: item?.item[0]?.contentType,
              promotion_name: item?.item[0]?.name || item.item[0]?.caption,
              promotion_id: item?.item[0]?.identifier,
            };
            this.googleTagManagerService.triggeringDotCmsPromotionGTMEvent(item.event, [promotionContent]);
          }
        })
      ),
    { dispatch: false }
  );
  /* View_Items */

  triggeringViewItemGTMEvent = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggeringViewItemGTMEvents, triggeringViewItemGTMEvent),
        mapToPayload(),
        map(item => {
          this.googleTagManagerService.triggeringListItemEvent(item.event, item.item);
        })
      ),
    { dispatch: false }
  );
  /*Add to Cart Event */
  addToCartEventGTMEvents$ = createEffect(() =>
    this.actions$.pipe(
      filter(() => this.isTrackingAllowed),
      ofType(addItemsToBasketSuccess),
      mapToPayload(),
      mergeMap(items => {
        // Trigger the GA event for 'add_to_cart' with the line items
        this.googleTagManagerService.triggeringListItemEvent('add_to_cart', items?.lineItems);
        return of({ type: '[Google Tag Manager] Triggering GTM Add to cart Event' }); // Return a no-op action or define another one if necessary
      })
    )
  );
  updateCartItemEventGTMEvents$ = createEffect(() =>
    this.actions$.pipe(
      filter(() => this.isTrackingAllowed),
      ofType(updateBasketItemSuccess),
      mapToPayload(),
      mergeMap(items => {
        // Trigger the GA event for 'add_to_cart' with the line items
        const lineItems = [items?.lineItem];
        this.googleTagManagerService.triggeringListItemEvent('add_to_cart', lineItems);
        return of({ type: '[Google Tag Manager] Triggering GTM Add to cart Event' }); // Return a no-op action or define another one if necessary
      })
    )
  );
  /* Remove from Cart */
  removeFromCartItemsGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggerGTMRemoveToCart),
        withLatestFrom(this.store.pipe(select(getCurrentBasket))),
        map(([_, basket]) => {
          this.googleTagManagerService.triggeringListItemEvent('remove_from_cart', basket?.lineItems);
        })
      ),
    { dispatch: false }
  );

  removeFromCartItemGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(deleteBasketItem),
        mapToPayloadProperty('itemId'),
        distinctUntilChanged(),
        map(itemId => {
          const lineItems = this.googleTagManagerService.getBasketItemDetails(itemId);
          this.googleTagManagerService.triggeringListItemEvent('remove_from_cart', lineItems);
        })
      ),
    { dispatch: false }
  );
  /*Search Event */
  searchGTMEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggeringSearchGTMEvent),
      mapToPayloadProperty('searchTerm'),
      concatMap(searchTypeTerm => {
        const searchValue = searchTypeTerm?.split(',');
        const dataLayerVariable: DataLayerObject = {
          ecommerce: {
            search_term: searchValue[0],
            search_selected_product: searchValue.some(search => search === 'product') ? searchValue[1] : undefined,
            search_selected_category: searchValue.some(search => search === 'category') ? searchValue[1] : undefined,
            search_selected_brand: searchValue.some(search => search === 'brand') ? searchValue[1] : undefined,
          },
        };
        this.googleTagManagerService.triggeringSearchEvent('search', dataLayerVariable);
        return of({ type: '[Google Tag Manager] Triggering GTM Search Event' }); // Return a no-op action or define another one if necessary
      })
    )
  );
  /* Checkout */
  //Begin-checkout
  beginCheckoutItemsGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggerBeginCheckoutGTMEvent),
        map(() => {
          this.googleTagManagerService.triggeringEventOfUserIdEvent('begin_checkout');
        })
      ),
    { dispatch: false }
  );
  //Payment method
  paymentMethodGTMEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggerPaymentMethodGTMEvent),
        mapToPayload(),
        withLatestFrom(this.store.pipe(select(getCurrentBasket))),
        map(([_, basket]) =>
          this.googleTagManagerService.triggeringListItemEvent('add_payment_info', basket?.lineItems)
        )
      ),
    { dispatch: false }
  );
  //Shipping Method and Payment Method event for Multiple Shipping page checkout
  shippingMethodGAEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggerShippingMethodGTMEvent),
        mapToPayloadProperty('shipping'),
        withLatestFrom(this.store.pipe(select(getCurrentBasket))),
        map(([_, basket]) => {
          this.googleTagManagerService.triggeringListItemEvent('add_shipping_info', basket?.lineItems);
        })
      ),
    { dispatch: false }
  );
  //Review-checkout
  reviewCheckoutItemsGTMEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(triggerReviewCheckoutGTMEvent),
        withLatestFrom(this.store.pipe(select(getCurrentBasket))),
        map(([_, basket]) => {
          this.googleTagManagerService.triggeringListItemEvent('review_checkout', basket.lineItems);
        })
      ),
    { dispatch: false }
  );
  reviewCheckoutEventGTMEvents$ = createEffect(() =>
    this.actions$.pipe(
      filter(() => this.isTrackingAllowed),
      ofType(updateBasketItemSuccess),
      mapToPayload(),
      withLatestFrom(this.store.pipe(select(getCurrentBasket))),
      take(1),
      mergeMap(([basketInfo, items]) =>
        this.store.pipe(
          select(selectCleanUrl),
          map(url => ({ basketInfo, items, url }))
        )
      ),
      mergeMap(({ basketInfo, items, url }) => {
        // Trigger the GA event for 'add_to_cart' with the line items
        if (url === 'one-page-checkout') {
          const lineItemArray: LineItem[] = [basketInfo.lineItem];
          this.googleTagManagerService.triggeringListItemEvent('add_to_cart', lineItemArray);
          this.googleTagManagerService.triggeringListItemEvent('review_checkout', items?.lineItems);
        }
        return of({ type: '[Google Tag Manager] GTM Add To Cart Event Triggers' });
      })
    )
  );
  //Purchase
  purchaseGTMEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(createOrderSuccess, createMultipleShippingOrderSuccess),
        mapToPayloadProperty('order'),
        map(order => {
          this.googleTagManagerService.triggeringPurchaseGTMEvent(order);
        })
      ),
    { dispatch: false }
  );
  //Requisition Approving Action
  requisitionApprovingPurchaseEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        filter(() => this.isTrackingAllowed),
        ofType(updateRequisitionStatusSuccess),
        mapToPayloadProperty('requisition'),
        map(requisition => {
          if (requisition.approval.statusCode === 'APPROVED') {
            this.googleTagManagerService.triggeringListItemEvent(
              'add_shipping_info',
              requisition?.lineItems,
              requisition?.commonShippingMethod?.name
            );
            this.googleTagManagerService.triggeringListItemEvent(
              'add_payment_info',
              requisition?.lineItems,
              requisition?.payment?.displayName
            );
            this.googleTagManagerService.triggeringPurchaseGTMEvent(requisition as unknown as Order);
          }
        })
      ),
    { dispatch: false }
  );
  // Effect to listen for NavigationStart
  navigationStartEffect$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((event: Event) => event instanceof NavigationEnd && this.isTrackingAllowed),
        mergeMap(event => {
          // Dispatch your custom action with payload if needed
          const navigationAction = event as NavigationEnd;

          const regex = /^([^?]+)/;
          const match = navigationAction.url.match(regex);
          return combineLatest([
            this.store.pipe(select(getCurrentBasket)),
            this.store.pipe(select(selectPreviousUrl)),
          ]).pipe(
            filter(([basket, _]) => basket !== undefined),
            take(1),
            map(([basket, previousUrl]) => {
              if (/\/one-page-checkout(?!\/)/.test(navigationAction.url)) {
                this.googleTagManagerService.triggeringEventOfUserIdEvent('begin_checkout');
              } else {
                switch (match[1]) {
                  case '/multiple-shipping-checkout/payment':
                    this.googleTagManagerService.triggeringListItemEvent('add_shipping_info', basket?.lineItems);
                    break;
                  case '/multiple-shipping-checkout/review':
                    const cleanUrl = previousUrl.match(regex);
                    if (cleanUrl && cleanUrl[1] === '/multiple-shipping-checkout/payment') {
                      this.googleTagManagerService.triggeringListItemEvent('add_payment_info', basket?.lineItems);
                    } else {
                      this.googleTagManagerService.triggeringListItemEvent('add_shipping_info', basket?.lineItems);
                      this.googleTagManagerService.triggeringListItemEvent('add_payment_info', basket?.lineItems);
                    }
                    break;
                }
              }
            })
          );
        })
      ),
    { dispatch: false }
  );
}
